import { useEffect, useState } from "react";
import {
  UilPlus,
  UilSearch,
} from "@iconscout/react-unicons";
import moment from "moment";
import * as Yup from "yup";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import AuthLayout from "../../components/auth/AuthLayout";
import VerifiedGuard from "../../components/auth/VerifiedGuard";
import { useTranslation } from "react-i18next";
import { GSM } from "../../models/Package";
import { UilTrashAlt, UilEditAlt } from '@iconscout/react-unicons';
import { smsService } from "../../services/smsService";

type Props = {};

const GsmPricingList = (props: Props) => {
  const {t} = useTranslation();
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState<number | "all">(5); 
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedPackage, setSelectedPackage] = useState<GSM | null>(null);
  const [pricingRules, setPricingRules] = useState<GSM[]>([])
  const [selectedToDelete, setSelectedToDelete] = useState<GSM | null>(null);

  const [isEditMode, setIsEditMode] = useState(false);
  
  const handleDelete = async (id: number | undefined) => {
    if (!id) return;
    try {
      setLoading(true);
      await smsService.deletePricingRule(id.toString());
      toast.success(t("gsm.deleteSuccess"));
      fetchPricingRules(); 
    } catch (error) {
      toast.error(t("gsm.deleteError"));
    } finally {
      setLoading(false);
    }
  };
  
  
  const fetchPricingRules = async () => {
    try {
      setLoading(true);
      const response = await smsService.getPricingRules(); 
      setPricingRules(response);
    } catch (error) {
      toast.error(t("gsm.fetchError"));
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (data: GSM) => {
    try {
      setLoading(true);
      if (isEditMode && selectedPackage?.id) {
        const updateData = {
          ...data,
          id: selectedPackage.id,
        };
        await smsService.updatePricingRule(selectedPackage.id.toString(), updateData);
        toast.success(t("gsm.updateSuccess"));
      } else {
        await smsService.createPricingRule(data);
        toast.success(t("gsm.createSuccess"));
      }
  
      fetchPricingRules();
      formik.resetForm();
      setSelectedPackage(null);
      setIsEditMode(false);
    } catch (error) {
      toast.error(t("gsm.generalError"));
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelectPackage = (data: GSM) => {
    setSelectedPackage(data);
    setIsEditMode(true);
    formik.setValues(data);
  };
  
  const formik = useFormik({
    initialValues: {
      minQuantity: 0,
      unitPrice: 0,
    },
    validationSchema: Yup.object({
      minQuantity: Yup.number().required(t("gsm.required")),
      unitPrice: Yup.number().required(t("gsm.required")),
    }),
    onSubmit: handleFormSubmit,
  });
 
  const filteredRules = pricingRules.filter((rule) => {
    const dateFormatted = moment(rule.createdAt).format("DD/MM/YYYY HH:mm");
    return (
      rule.minQuantity.toString().includes(searchQuery.toLowerCase()) ||
      rule.unitPrice.toString().includes(searchQuery.toLowerCase()) ||
      dateFormatted.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });
    
  useEffect(() => {
    fetchPricingRules();
  }, [limit]);

  return (
    <AuthLayout>
      <VerifiedGuard>
        <main className="nxl-container">
          <div className="nxl-content">
            <div className="page-header">
              <div className="page-header-left d-flex align-items-center">
                <div className="page-header-title">
                  <h5 className="m-b-10 border-0">{t("gsm.manageTitle")}</h5>
                </div>
              </div>
              <div className="page-header-right ms-auto">
                <div className="page-header-right-items">
                  <div className="d-flex align-items-center gap-2 page-header-right-items-wrapper">
                    <button
                        data-bs-toggle="modal"
                        data-bs-target="#addPlanModal"
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                        setIsEditMode(false);
                        formik.resetForm();
                        }}
                    >
                        <UilPlus />
                        <span>{t("gsm.addPricing")}</span>
                    </button>
                  </div>
                </div>
                <div className="d-md-none d-flex align-items-center">
                  <div className="page-header-right-open-toggle">
                    <i className="feather-align-right fs-20"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="main-content">
            <div className="d-flex justify-content-between align-items-center mb-1 ">
            <div style={{ position: "relative" }}>
              <input
                type="text"
                className="form-control"
                placeholder={t("gsm.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ paddingLeft: "40px" }} 
              />
              <div
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                }}
              >
                <UilSearch className="text-muted" />
              </div>
            </div>
            <div>
              <select
                className="form-select"
                style={{ width: "auto" }}
                value={limit}
                onChange={(e) =>
                  setLimit(
                    e.target.value === "all"
                      ? "all"
                      : Number(e.target.value)
                  )
                }
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value="all">{t("gsm.all")}</option>
              </select>
            </div>
            </div>
              <div className="row">
                <div className="col-lg-12">
                  <div className="card stretch stretch-full">
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover" id="customerList">
                          <thead className="text-center">
                            <tr>
                              <th className="text-nowrap" style={{ width: "20%" }}>{t("gsm.minimumMessages")}</th>
                              <th className="text-nowrap" style={{ width: "20%" }}>{t("gsm.price")}</th>
                              <th className="text-nowrap" style={{ width: "20%" }}>{t("gsm.dateCreated")}</th>
                              <th className="text-nowrap" style={{ width: "20%" }}>{t("gsm.option")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRules?.map((rule, index) => (
                                <tr key={index}>
                                <td className="align-middle text-center">
                                    {rule.minQuantity}
                                </td>
                                <td className="align-middle text-center">
                                    {rule.unitPrice} €
                                </td>
                                <td className="align-middle text-center">
                                    {moment(rule.createdAt).format("DD/MM/YYYY HH:mm")}
                                </td>
                                <td className="align-middle text-center">
                                    <div className="d-flex justify-content-center gap-2">
                                    <button
                                       className="btn btn-warning btn-sm"
                                       data-bs-toggle="modal"
                                       data-bs-target="#addPlanModal"
                                       onClick={() => handleSelectPackage(rule)}
                                    >
                                        <UilEditAlt/>{t("gsm.edit")}
                                    </button>
                                    <button
                                        className="btn btn-danger btn-sm"
                                        data-bs-toggle="modal"
                                        data-bs-target="#deleteModal"
                                        onClick={() => setSelectedToDelete(rule)}
                                    >
                                        <UilTrashAlt/>{t("gsm.delete")}
                                    </button>
                                    </div>
                                </td>
                                </tr>
                            ))}
                        </tbody>
                        </table>
                        {pricingRules?.length === 0 && (
                          <h5 className="text-center my-5">
                            {t("gsm.emptyList")}
                          </h5>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <div
          className="modal fade"
          id="addPlanModal"
          tabIndex={-1}
          aria-labelledby="addPlanModal"
          aria-hidden="true"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">
                  {isEditMode ? (
                    <>
                      {t("gsm.edit")} {" "}
                      <span className="text-primary">
                        {t("gsm.pricing")}
                      </span>
                    </>
                  ) : (
                    t("gsm.addNewPricing")
                  )}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-sm"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => setSelectedPackage(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-6">
                    <label htmlFor="minQuantity">{t("gsm.messageRange")}</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder={t("gsm.messageRange")}
                      id="minQuantity"
                      {...formik.getFieldProps("minQuantity")}
                    />
                    {formik.touched.minQuantity && formik.errors.minQuantity ? (
                      <div className="text-danger">{formik.errors.minQuantity}</div>
                    ) : null}
                  </div>
                 
                  <div className="mb-3 col-md-6">
                    <label htmlFor="price">{t("gsm.priceEuro")}</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder={t("gsm.pricePlaceholder")}
                      id="unitPrice"
                      {...formik.getFieldProps("unitPrice")}
                    />
                    {formik.touched.unitPrice && formik.errors.unitPrice ? (
                      <div className="text-danger">{formik.errors.unitPrice}</div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  data-bs-dismiss="modal"
                  onClick={() => setSelectedPackage(null)}
                >
                  {t("gsm.close")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => formik.handleSubmit()}
                  disabled={loading || !formik.isValid}
                  data-bs-dismiss="modal"
                >
                  {loading && <span className="loader"></span>}
                  {isEditMode ? t("gsm.save") : t("gsm.save")}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          className="modal fade"
          id="deleteModal"
          tabIndex={-1}
          aria-labelledby="deleteModal"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">
                  {t("gsm.delete")} {t("gsm.pricing")}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-sm"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                {t("gsm.deleteConfirm")} {" "}
                <span className="fw-bold">
                 {selectedToDelete?.minQuantity} {t("gsm.msg")} @ {selectedToDelete?.unitPrice} €
                </span>{" "}
                ?
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  data-bs-dismiss="modal"
                >
                  {t("gsm.cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  disabled={loading}
                  onClick={() => {
                    handleDelete(selectedToDelete?.id);
                    setSelectedToDelete(null);
                  }}
                  data-bs-dismiss="modal"
                >
                  {t("gsm.delete")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </VerifiedGuard>
    </AuthLayout>
  );
};

export default GsmPricingList;